import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import Modal from "react-modal";
import { t } from "ttag";

import { FinancingModalTrigger } from "../../../apps/financing/FinancingModalTrigger";
import RichText from "../../../common/RichText";
import { IWebPageURL } from "../../../models/nominals";
import { IServiceFooterValueProps } from "../../../models/value-props.interfaces";
import { isMobile } from "../../../utils/detectMobile";
import { getFooterSettings } from "../../../utils/settings";
import { ContactUsModal } from "./ContactUsModal";
import { IEmailModalSnippet } from "./EmailCaptureContent";
import { EmailModal } from "./EmailModal";
import { ServiceFooterValuePropsModal } from "./ServiceFooterValuePropsModal";

import styles from "./ServiceFooter.module.scss";

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

export interface IServiceFooterSnippetProps {
    title: string;
    type: string;
    icon?: string;
    content: IServiceFooterValueProps | IEmailModalSnippet | null;
    trigger: string;
    trigger_mobile: string;
}

interface IProps {
    interval: number | null;
    loops: number | null;
    snippets: IServiceFooterSnippetProps[];
    chatOfflineLink?: IWebPageURL;
}

interface IState {
    modalContactIsOpen: boolean;
    modalPropsIsOpen: boolean;
    modalEmailIsOpen: boolean;
}

// TODO(ssr, LEVEL 8 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 8/10 due to complex Flickity carousel integration, multiple lifecycle methods, timers, modal state management, and extensive event handling.
export class ServiceFooter extends React.Component<IProps, IState> {
    private carouselFlickity: Flickity | null = null;
    private snippetsElem: HTMLDivElement | null = null;
    private carouselTimer: number | null = null;

    public state: IState = {
        modalContactIsOpen: false,
        modalPropsIsOpen: false,
        modalEmailIsOpen: false,
    };

    componentDidMount(): void {
        if (!this.snippetsElem) {
            return;
        }

        this.initCarousel(this.snippetsElem);
    }

    componentWillUnmount(): void {
        if (this.carouselFlickity) {
            if (this.carouselTimer) {
                window.clearTimeout(this.carouselTimer);
            }

            this.carouselFlickity.destroy();
        }
    }

    private initCarousel(carousel: HTMLDivElement): void {
        if (carousel.childNodes.length > 1) {
            this.carouselFlickity = new Flickity(carousel, {
                cellAlign: "center",
                pageDots: false,
                prevNextButtons: true,
                imagesLoaded: true,
                arrowShape: {
                    x0: 20,
                    x1: 60,
                    y1: 50,
                    x2: 60,
                    y2: 30,
                    x3: 35,
                },
                autoPlay: this.props.interval ? this.props.interval : true,
                pauseAutoPlayOnHover: true,
                wrapAround: true,
            });

            if (this.props.loops) {
                const interval = this.props.interval
                    ? this.props.interval
                    : 3000;
                const slideNum = this.props.snippets.length;
                const duration = interval * slideNum * this.props.loops;

                this.carouselTimer = window.setInterval(() => {
                    this.carouselFlickity?.stopPlayer();
                }, duration);
            }

            carousel.addEventListener("mouseover", () => {
                this.carouselFlickity?.stopPlayer();
            });
            carousel.addEventListener("focus", () => {
                this.carouselFlickity?.stopPlayer();
            });
        }
    }

    private readonly onOpenModalContact = (
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalContactIsOpen: true,
        });
    };

    private readonly onOpenModalProps = (
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalPropsIsOpen: true,
        });
    };

    private readonly onOpenModalEmail = (
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalEmailIsOpen: true,
        });
    };

    private readonly onCloseModalContact = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalContactIsOpen: false,
        });
    };

    private readonly onCloseModalProps = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalPropsIsOpen: false,
        });
    };

    private readonly onCloseModalEmail = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        this.setState({
            modalEmailIsOpen: false,
        });
    };

    private renderSnippetContent(
        trigger: IServiceFooterSnippetProps,
        icon?: string,
    ): React.JSX.Element {
        const content =
            isMobile.any() && trigger.trigger_mobile.length > 0
                ? trigger.trigger_mobile
                : trigger.trigger;

        return (
            <>
                {icon && <img alt="" src={icon} />}
                <RichText inline={true} html={content} />
            </>
        );
    }

    private renderSnippetButton(
        isCarousel: boolean,
        snippet: IServiceFooterSnippetProps,
        onClick?: React.MouseEventHandler<HTMLButtonElement>,
    ): React.JSX.Element {
        const classes = classNames({
            [styles.trigger]: true,
            [styles.carouselTrigger]: isCarousel,
        });

        return (
            <button className={classes} onClick={onClick ? onClick : undefined}>
                {this.renderSnippetContent(snippet, snippet.icon)}
            </button>
        );
    }

    private renderSnippets(): React.JSX.Element[] {
        const isCarousel = this.props.snippets.length > 1;

        return this.props.snippets.map((snippet, i) => {
            switch (snippet.type) {
                case "default":
                    return (
                        <React.Fragment key={i}>
                            {this.renderSnippetButton(isCarousel, snippet)}
                        </React.Fragment>
                    );
                case "email":
                    return (
                        <React.Fragment key={i}>
                            {this.renderSnippetButton(
                                isCarousel,
                                snippet,
                                this.onOpenModalEmail,
                            )}
                            <EmailModal
                                isOpen={this.state.modalEmailIsOpen}
                                onClose={this.onCloseModalEmail}
                                content={snippet.content as IEmailModalSnippet}
                            />
                        </React.Fragment>
                    );
                case "prequal":
                    const classes = classNames({
                        [styles.financingModalTrigger]: true,
                        "al-service-footer-finance-block__pre-qualify": true,
                        [styles.carouselTrigger]: isCarousel,
                    });
                    return (
                        <FinancingModalTrigger
                            key={`Trigger-${i}`}
                            modalType="prequal-app"
                            className={classes}
                        >
                            {this.renderSnippetContent(snippet, snippet.icon)}
                        </FinancingModalTrigger>
                    );
                case "value_prop":
                    return (
                        <React.Fragment key={i}>
                            {this.renderSnippetButton(
                                isCarousel,
                                snippet,
                                this.onOpenModalProps,
                            )}
                            <ServiceFooterValuePropsModal
                                isOpen={this.state.modalPropsIsOpen}
                                onClose={this.onCloseModalProps}
                                content={
                                    snippet.content as IServiceFooterValueProps
                                }
                            />
                        </React.Fragment>
                    );
                default:
                    // TODO(i18n): Use t tagged template for error message: t`Unexpected service footer snippet type: ${snippet.type}`
                    throw new Error(
                        `Unexpected service footer snippet type: ${snippet.type}`,
                    );
            }
        });
    }

    render(): React.JSX.Element {
        const contactUs = !!getFooterSettings() && (
            <div
                className={`${styles.contactUs} service-footer__contact-us al-service-footer__buttons`}
            >
                <button onClick={this.onOpenModalContact}>
                    {t`Contact Us`}
                </button>
            </div>
        );

        return (
            <div className={`${styles.root} service-footer al-service-footer`}>
                <div className={`${styles.content} l-capped-width`}>
                    <div
                        className={styles.snippets}
                        ref={(ref) => {
                            this.snippetsElem = ref;
                        }}
                    >
                        {this.renderSnippets()}
                    </div>
                    {contactUs}
                </div>
                <ContactUsModal
                    isOpen={this.state.modalContactIsOpen}
                    onClose={this.onCloseModalContact}
                    chatOfflineLink={this.props.chatOfflineLink}
                />
                <div className={styles.iphoneXbg}></div>
            </div>
        );
    }
}
